import * as Yup from 'yup';

// Define validation rules per type

const validationRules = {
  string: (field, validation) => {
    let schema = Yup.string().nullable();

    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.minLength) {
      schema = schema.min(
        validation.minLength,
        `${field.label} must be at least ${validation.minLength} characters`
      );
    }
    if (validation?.maxLength) {
      schema = schema.max(
        validation.maxLength,
        `${field.label} must be at most ${validation.maxLength} characters`
      );
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value => !value || new RegExp(validation.pattern).test(value)
      });
    }
    return schema;
  },

  number: (field, validation) => {
    let schema = Yup.number()
      .transform(
        (value, originalValue) => (originalValue === '' ? null : value) // Convert "" to null
      )
      .nullable()
      .typeError(`${field.label} must be a number`);

    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.minValue !== undefined) {
      schema = schema.min(
        validation.minValue,
        `${field.label} must be at least ${validation.minValue}`
      );
    }
    if (validation?.maxValue !== undefined) {
      schema = schema.max(
        validation.maxValue,
        `${field.label} must be at most ${validation.maxValue}`
      );
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value =>
          value === null ||
          value === undefined ||
          (typeof value === 'number' &&
            new RegExp(validation.pattern).test(String(value)))
      });
    }
    return schema;
  },

  date: (field, validation) => {
    let schema = Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        // Return null if empty string or invalid date
        return originalValue === '' || value === 'Invalid Date' ? null : value;
      })
      .typeError(`${field.label} must be a valid date`);

    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value => !value || new RegExp(validation.pattern).test(value)
      });
    }
    return schema;
  },

  email: (field, validation) => {
    let schema = Yup.string().nullable().email('Invalid email format');

    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value => !value || new RegExp(validation.pattern).test(value)
      });
    }
    return schema;
  },

  dropdown: (field, validation) => {
    if (field.multiSelect) {
      let schema = Yup.array()
        .nullable()
        .transform((value, originalValue) => originalValue || []);

      if (validation?.required) {
        schema = schema
          .min(1, `Please select at least one ${field.label.toLowerCase()}`)
          .required(`${field.label} is required`);
      }
      return schema;
    }

    let schema = Yup.string().nullable();
    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    return schema;
  },

  textAndUnit: (field, validation) => {
    const objectShape = {
      // Quantity field validation - Accept both string and number
      quantity: Yup.mixed()
        .nullable()
        .transform((value, originalValue) => {
          // Handle various input types
          if (
            originalValue === '' ||
            originalValue === null ||
            originalValue === undefined
          ) {
            return null;
          }

          // If it's already a number, convert to string to ensure consistency
          if (typeof originalValue === 'number') {
            return originalValue.toString();
          }

          // Clean up string input
          if (typeof originalValue === 'string') {
            // Remove quotes if present
            const cleaned =
              originalValue.startsWith('"') && originalValue.endsWith('"')
                ? originalValue.slice(1, -1)
                : originalValue;

            // Return the cleaned string (not parsing to number)
            return cleaned;
          }

          // If we got here, convert to string for consistency
          return String(value);
        })
        // Define a custom test to validate it's a valid number
        .test(
          'is-valid-number',
          `${field.label} quantity must be a valid number`,
          value => {
            if (value === null || value === undefined || value === '')
              return true;
            const numValue = parseFloat(value);
            return !isNaN(numValue) && isFinite(numValue);
          }
        ),

      // Unit field validation (string/dropdown selection)
      unit: Yup.string()
        .nullable()
        .transform((value, originalValue) => {
          // Clean up any extra quotes
          if (
            typeof originalValue === 'string' &&
            originalValue.startsWith('"') &&
            originalValue.endsWith('"')
          ) {
            return originalValue.slice(1, -1);
          }
          return originalValue;
        })
    };

    // Apply required validation if specified
    if (validation?.required) {
      objectShape.quantity = objectShape.quantity.required(
        `${field.label} quantity is required`
      );
      objectShape.unit = objectShape.unit.required(
        `${field.label} unit is required`
      );
    }

    // Apply additional validation rules for quantity if specified
    if (validation?.minValue !== undefined) {
      objectShape.quantity = objectShape.quantity.test(
        'min-value',
        `${field.label} quantity must be at least ${validation.minValue}`,
        value => {
          if (value === null || value === undefined || value === '')
            return true;
          const numValue = parseFloat(value);
          return !isNaN(numValue) && numValue >= validation.minValue;
        }
      );
    }
    if (validation?.maxValue !== undefined) {
      objectShape.quantity = objectShape.quantity.test(
        'max-value',
        `${field.label} quantity must be less than or equal to ${validation.maxValue}`,
        value => {
          if (value === null || value === undefined || value === '')
            return true;
          const numValue = parseFloat(value);
          return !isNaN(numValue) && numValue <= validation.maxValue;
        }
      );
    }

    let schema = Yup.object().shape(objectShape);

    // Make the entire object required if validation requires it
    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }

    return schema;
  },
  contact_number: (field, validation) => {
    let schema = Yup.string()
      .nullable()
      .matches(/^\d*$/, 'Phone number must only contain digits');

    if (validation?.required) {
      schema = schema.required(`${field.label} is required`);
    }
    if (validation?.minLength) {
      schema = schema.min(
        validation.minLength,
        `${field.label} must be exactly ${validation.minLength} digits`
      );
    }
    if (validation?.maxLength) {
      schema = schema.max(
        validation.maxLength,
        `${field.label} must be exactly ${validation.maxLength} digits`
      );
    }
    if (validation?.pattern) {
      schema = schema.test({
        name: 'pattern',
        message:
          validation?.messages?.pattern || `Invalid ${field.label} format`,
        test: value => !value || new RegExp(validation.pattern).test(value)
      });
    }
    return schema;
  },
  minmax: (field, validation) => {
    return Yup.object().shape({
      min: Yup.number()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value
        )
        .nullable()
        .typeError(`${field.label} Min must be a number`)
        .min(0, `${field.label} Min must be greater than or equal to 0`)
        .test(
          'both-or-none',
          `${field.label} Min is required if Max is filled`,
          function (value) {
            const { max } = this.parent;
            return !max || value !== null; // Min is required only if Max is present
          }
        ),

      max: Yup.number()
        .transform((value, originalValue) =>
          originalValue === '' ? null : value
        )
        .nullable()
        .typeError(`${field.label} Max must be a number`)
        .test(
          'both-or-none',
          `${field.label} Max is required if Min is filled`,
          function (value) {
            const { min } = this.parent;
            return !min || value !== null; // Max is required only if Min is present
          }
        )
        .test(
          'greater-than-min',
          `${field.label} Max must be greater than Min`,
          function (value) {
            const { min } = this.parent;
            if (value === null || min === null) return true; // Skip validation if either is null
            return value >= min; // Ensure max is greater than or equal to min
          }
        )
    });
  },

  geo_location: (field, validation) => {
    if (!validation?.required) {
      return Yup.mixed().nullable();
    }

    return Yup.mixed()
      .nullable()
      .test('is-valid-location', `${field.label} is invalid`, value => {
        return (
          value !== null && typeof value === 'object' && !Array.isArray(value)
        );
      })
      .required(`${field.label} is required`);
  },

  coordinates: () => Yup.mixed().nullable(),

  table: field => {
    return Yup.array()
      .of(
        Yup.object().shape(
          field.table.columns.reduce((acc, column) => {
            if (column.validation) {
              acc[column.id] = getFieldValidator(column);
            }
            return acc;
          }, {})
        )
      )
      .min(1, `At least one row is required in ${field.table.title}`);
  },

  toggle_with_dropdown: field => {
    const schema = {
      [field.id]: Yup.string().required('Please select an option').nullable()
    };

    // Add dependent validations for dropdowns
    Object.entries(field.options).forEach(([key, dropdown]) => {
      schema[key] = Yup.string().when(field.id, {
        is: value => value === key,
        then: () =>
          Yup.string()
            .required(`Please select a ${dropdown.label.toLowerCase()}`)
            .nullable(),
        otherwise: () => Yup.string().nullable()
      });
    });

    return schema;
  },

  date_range: (field, validation) => {
    if (!validation?.required) {
      return Yup.mixed().nullable();
    }

    return Yup.mixed()
      .nullable()
      .test('is-valid-date_range', `${field.label} is invalid`, value => {
        return (
          value !== null && typeof value === 'object' && !Array.isArray(value)
        );
      })
      .required(`${field.label} is required`);
  },

  month_day_range: (field, validation) => {
    if (!validation?.required) {
      return Yup.mixed().nullable();
    }

    return Yup.mixed()
      .nullable()
      .test('is-valid-month_day_range', `${field.label} is invalid`, value => {
        return (
          value !== null && typeof value === 'object' && !Array.isArray(value)
        );
      })
      .required(`${field.label} is required`);
  },

  password: (field, validation) => {
    let schema = Yup.string().nullable();

    if (validation?.required) {
      schema = schema.required(`${field.label || 'Password'} is required`);
    }
    schema = schema
      .min(
        validation?.minLength || 8,
        `${field.label || 'Password'} must be at least ${
          validation?.minLength || 8
        } characters`
      )
      .matches(
        /[A-Z]/,
        validation?.messages?.uppercase ||
          `${
            field.label || 'Password'
          } must contain at least one uppercase letter`
      )
      .matches(
        /\d/,
        validation?.messages?.number ||
          `${field.label || 'Password'} must contain at least one number`
      )
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        validation?.messages?.specialCharacter ||
          `${
            field.label || 'Password'
          } must contain at least one special character`
      )
      .oneOf(
        [Yup.ref(validation?.reference || 'password_hash'), null],
        validation?.messages?.match ||
          `${field.label || 'Confirm Password'} must match Password`
      );

    return schema;
  },
  form_report: (field, validation) => {
    // Handle based on isMulti flag
    if (field.isMulti === false) {
      // For single value - use string validation
      let schema = Yup.string().nullable();

      if (validation?.required) {
        schema = schema.required(`${field.label} is required`);
      }

      return schema;
    } else {
      // For multi value (default) - use array validation
      let schema = Yup.array()
        .of(Yup.string().nullable())
        .nullable()
        .transform((value, originalValue) => originalValue || []);

      if (validation?.required) {
        schema = schema
          .min(1, `${field.label} must have at least one item`)
          .required(`${field.label} is required`);
      }

      return schema;
    }
  }
};

const getFieldValidator = field => {
  // Special case for fields with specific IDs
  // if (field.id === 'contact_number') {
  //   return validationRules.contact_number(field, field.validation);
  // }

  // Get the appropriate validation rule based on field type
  const validationRule = validationRules[field.type] || validationRules.string;
  return validationRule(field, field.validation);
};

export const generateValidationSchema = ({ fields, visibilityMap }) => {
  const schemaFields = {};
  const fieldList = Object.values(fields);
  fieldList.forEach(field => {
    // Skip validation for hidden fields
    if (visibilityMap && visibilityMap.get(field.id) === false) {
      return;
    }
    if (field.table) {
      schemaFields[field.id] = validationRules.table(field);
    } else if (field.type === 'toggle_with_dropdown') {
      Object.assign(schemaFields, validationRules.toggle_with_dropdown(field));
    } else if (field.modal) {
      // Object.assign(schemaFields, validationRules.toggle_with_dropdown(field));
    } else {
      schemaFields[field.id] = getFieldValidator(field);
    }
  });

  return Yup.object().shape(schemaFields);
};
